import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PlayerProvider } from "./context/PlayerContext";
import { AppShell } from "./components/AppShell";
import { Loading } from "./components/common";

import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import Onboarding from "./pages/Onboarding";
import Feed from "./pages/Feed";
import Explore from "./pages/Explore";
import Reels from "./pages/Reels";
import Music from "./pages/Music";
import TV from "./pages/TV";
import VideoPage from "./pages/VideoPage";
import Live from "./pages/Live";
import LiveRoom from "./pages/LiveRoom";
import Charts from "./pages/Charts";
import Industry from "./pages/Industry";
import Awards from "./pages/Awards";
import Profile from "./pages/Profile";
import Wallet from "./pages/Wallet";
import Messages from "./pages/Messages";
import Notifications from "./pages/Notifications";
import Studio from "./pages/Studio";
import Admin from "./pages/Admin";
import Plus from "./pages/Plus";
import Settings from "./pages/Settings";
import Hire from "./pages/Hire";
import Store from "./pages/Store";
import Collection from "./pages/Collection";
import Marketplace from "./pages/Marketplace";
import SellerStudio from "./pages/SellerStudio";
import Storefront from "./pages/Storefront";
import BuyerLibrary from "./pages/BuyerLibrary";
import Shop from "./pages/Shop";
import Businesses from "./pages/Businesses";
import Events from "./pages/Events";
import VirtuCity from "./pages/VirtuCity";
import VenuePage from "./pages/VenuePage";
import { VStreetsHome, VStreetsCrews, VStreetsCrew, VStreetsLeaderboard, VStreetsAdmin, VStreetsMapPage } from "./pages/VStreets";
import { Terms, Privacy, DataPolicy } from "./pages/Legal";
import SLPhone from "./pages/SLPhone";
import SLSetup from "./pages/SLSetup";
import FiveMStore from "./pages/FiveMStore";
import CrewsDiscover from "./pages/CrewsDiscover";
import CrewHQ from "./pages/CrewHQ";
import { PublicShell } from "./components/PublicShell";
import { PaymentSuccess, PaymentCancel } from "./pages/Payment";

function Protected({ children }) {
  const { user, ready } = useAuth();
  const loc = useLocation();
  if (!ready || user === null) return <div className="min-h-screen bg-[#030303]"><Loading /></div>;
  if (user === false) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  if (!user.onboarded && loc.pathname !== "/onboarding") return <Navigate to="/onboarding" replace />;
  return children;
}

function Shell({ children }) { return <AppShell>{children}</AppShell>; }

function UProfileRedirect() {
  const { username } = useParams();
  return <Navigate to={`/${username}`} replace />;
}

// Public-or-private profile: logged-out visitors see a read-only public profile.
function ProfileRoute() {
  const { user, ready } = useAuth();
  if (!ready || user === null) return <div className="min-h-screen bg-[#030303]"><Loading /></div>;
  if (user === false) return <PublicShell><Profile /></PublicShell>;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  return <Shell><Profile /></Shell>;
}

function Guest({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen bg-[#030303]"><Loading /></div>;
  if (user && user.onboarded) return <Navigate to="/home" replace />;
  if (user && !user.onboarded) return <Navigate to="/onboarding" replace />;
  return children;
}

// Public-or-private marketplace & storefront: logged-out visitors can browse.
function PublicOrShell({ children }) {
  const { user, ready } = useAuth();
  if (!ready || user === null) return <div className="min-h-screen bg-[#030303]"><Loading /></div>;
  if (user === false) return <PublicShell>{children}</PublicShell>;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  return <Shell>{children}</Shell>;
}

function App() {
  return (
    <AuthProvider>
      <PlayerProvider>
        <BrowserRouter>
          <Toaster position="top-center" theme="dark" richColors />
          <Routes>
            <Route path="/" element={<Guest><Landing /></Guest>} />
            <Route path="/login" element={<Guest><Auth mode="login" /></Guest>} />
            <Route path="/register" element={<Guest><Auth mode="register" /></Guest>} />
            <Route path="/forgot" element={<Auth mode="forgot" />} />
            <Route path="/reset-password" element={<Auth mode="reset" />} />
            <Route path="/legal/terms" element={<Terms />} />
            <Route path="/legal/privacy" element={<Privacy />} />
            <Route path="/legal/data" element={<DataPolicy />} />
            <Route path="/sl/phone" element={<SLPhone />} />
            <Route path="/sl/setup" element={<SLSetup />} />
            <Route path="/city/phone" element={<SLPhone />} />
            <Route path="/fivem" element={<FiveMStore />} />
            <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />

            <Route path="/home" element={<Protected><Shell><Feed /></Shell></Protected>} />
            <Route path="/explore" element={<Protected><Shell><Explore /></Shell></Protected>} />
            <Route path="/reels" element={<Protected><Reels /></Protected>} />
            <Route path="/music" element={<Protected><Shell><Music /></Shell></Protected>} />
            <Route path="/tv" element={<Protected><Shell><TV /></Shell></Protected>} />
            <Route path="/video/:id" element={<Protected><Shell><VideoPage /></Shell></Protected>} />
            <Route path="/live" element={<Protected><Shell><Live /></Shell></Protected>} />
            <Route path="/live/:id" element={<Protected><Shell><LiveRoom /></Shell></Protected>} />
            <Route path="/charts" element={<Protected><Shell><Charts /></Shell></Protected>} />
            <Route path="/industry" element={<Protected><Shell><Industry /></Shell></Protected>} />
            <Route path="/hire" element={<Protected><Shell><Hire /></Shell></Protected>} />
            <Route path="/businesses" element={<Protected><Shell><Businesses /></Shell></Protected>} />
            <Route path="/events" element={<Protected><Shell><Events /></Shell></Protected>} />
            <Route path="/virtucity" element={<Protected><Shell><VirtuCity /></Shell></Protected>} />
            <Route path="/virtucity/venue/:id" element={<Protected><Shell><VenuePage /></Shell></Protected>} />
            <Route path="/vstreets" element={<Protected><Shell><VStreetsHome /></Shell></Protected>} />
            <Route path="/vstreets/dashboard" element={<Protected><Shell><VStreetsHome /></Shell></Protected>} />
            <Route path="/vstreets/map" element={<Protected><Shell><VStreetsMapPage /></Shell></Protected>} />
            <Route path="/vstreets/crews" element={<Protected><Shell><VStreetsCrews /></Shell></Protected>} />
            <Route path="/vstreets/leaderboard" element={<Protected><Shell><VStreetsLeaderboard /></Shell></Protected>} />
            <Route path="/vstreets/admin" element={<Protected><Shell><VStreetsAdmin /></Shell></Protected>} />
            <Route path="/vstreets/crew/:crewId/*" element={<Protected><Shell><VStreetsCrew /></Shell></Protected>} />
            <Route path="/shop" element={<Protected><Shell><Shop /></Shell></Protected>} />
            <Route path="/store" element={<Protected><Shell><Store /></Shell></Protected>} />
            <Route path="/collection" element={<Protected><Shell><Collection /></Shell></Protected>} />
            <Route path="/marketplace" element={<PublicOrShell><Marketplace /></PublicOrShell>} />
            <Route path="/seller" element={<Protected><Shell><SellerStudio /></Shell></Protected>} />
            <Route path="/library" element={<Protected><Shell><BuyerLibrary /></Shell></Protected>} />
            <Route path="/crews" element={<Protected><Shell><CrewsDiscover /></Shell></Protected>} />
            <Route path="/crew/:handle" element={<Protected><Shell><CrewHQ /></Shell></Protected>} />
            <Route path="/awards" element={<Protected><Shell><Awards /></Shell></Protected>} />
            <Route path="/wallet" element={<Protected><Shell><Wallet /></Shell></Protected>} />
            <Route path="/messages" element={<Protected><Shell><Messages /></Shell></Protected>} />
            <Route path="/notifications" element={<Protected><Shell><Notifications /></Shell></Protected>} />
            <Route path="/studio" element={<Protected><Shell><Studio /></Shell></Protected>} />
            <Route path="/plus" element={<Protected><Shell><Plus /></Shell></Protected>} />
            <Route path="/settings" element={<Protected><Shell><Settings /></Shell></Protected>} />
            <Route path="/admin" element={<Protected><Shell><Admin /></Shell></Protected>} />
            <Route path="/u/:username" element={<UProfileRedirect />} />
            <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
            <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />

            <Route path="/:handle/store" element={<PublicOrShell><Storefront /></PublicOrShell>} />
            <Route path="/:handle" element={<ProfileRoute />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </PlayerProvider>
    </AuthProvider>
  );
}

export default App;
